import { useTimeframeDifference } from '@griply/hooks';
import { Goal, Timestamp } from '@griply/types';
import { computeGoalSubgoalProgress } from '@griply/utils';
import { useSubGoals } from 'features/goals';
import { useTasks } from 'features/tasks';
import { useMemo } from 'react';

export type UseGoalSubgoalProgressOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
};

export const useGoalSubgoalProgress = (
  goal: Goal,
  { startDate, endDate }: UseGoalSubgoalProgressOptions,
) => {
  const subGoals = useSubGoals(goal.id, true);
  const allTasks = useTasks(
    { goals: [goal.id, ...subGoals.map(({ id }) => id)] },
    !!goal.id,
  );

  const { entries, target } = useMemo(
    () => computeGoalSubgoalProgress({ goal, subGoals, tasks: allTasks }),
    [goal, subGoals, allTasks],
  );

  const timeframeDifference = useTimeframeDifference({
    entries,
    startDate,
    endDate,
    target,
  });

  const lastEntryValue = entries[entries.length - 1]?.value;
  const completionPercentage = lastEntryValue
    ? Math.floor((lastEntryValue / subGoals.length) * 100)
    : 0;

  return {
    entries,
    completionPercentage,
    completed: subGoals.filter(({ completedAt }) => !!completedAt).length,
    target,
    timeframeDifference,
  };
};
